/**
 * Who asked for what in each week, for the admin reviewing a schedule
 * (owner, 24 October 2026): a column on the draft that reads "Dr. Chen ›
 * Vacation", so the admin can check the schedule against the requests at a
 * glance. Admins only; the data comes from the admin availability endpoint.
 *
 * What is listed, per physician and week:
 * - the admin's entry, if there is one. It is what counts, and a request
 *   under it does not count while it is there (see cells.ts), so the
 *   request is left out;
 * - otherwise every request that is not declined: an approved one plainly,
 *   one awaiting approval marked as such, since it was not used;
 * - a declined request is left out: it never counts.
 *
 * Pure, so it is tested without a browser.
 */
import { cellStates, type EntryKind, type GridCell } from './cells.ts';

export interface WeekRequestLine {
  physicianId: string;
  kind: EntryKind;
  /** A request still awaiting approval: shown, but it was not used. */
  pending: boolean;
}

/**
 * The lines for every week that has any, keyed by the week's Monday. Within
 * a week the physicians follow `order` (the roster's order), so a physician
 * sits in the same place from week to week; anyone not in it comes last.
 */
export function requestLinesByWeek(cells: readonly GridCell[], order: readonly string[]): Map<string, WeekRequestLine[]> {
  const rank = new Map(order.map((id, i) => [id, i]));
  const byWeek = new Map<string, WeekRequestLine[]>();
  for (const [key, state] of cellStates(cells)) {
    const [physicianId = '', weekStart = ''] = key.split('|');
    const lines: WeekRequestLine[] =
      state.entry !== null
        ? [{ physicianId, kind: state.entry, pending: false }]
        : state.requests.filter((r) => r.status !== 'declined').map((r) => ({ physicianId, kind: r.kind, pending: r.status === 'pending' }));
    if (lines.length === 0) continue;
    byWeek.set(weekStart, [...(byWeek.get(weekStart) ?? []), ...lines]);
  }
  const place = (id: string) => rank.get(id) ?? order.length;
  for (const lines of byWeek.values()) {
    // Stable, so one physician's approved request stays before their pending one.
    lines.sort((a, b) => place(a.physicianId) - place(b.physicianId) || (a.physicianId < b.physicianId ? -1 : a.physicianId > b.physicianId ? 1 : 0));
  }
  return byWeek;
}
